import * as R from 'ramda';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import React, { useState, useEffect, useCallback } from 'react';
// components
import { Table } from '../../components/table';
import { TextComponent } from '../../components/text';
import { PageTitle } from '../../components/page-title';
import { openLoader, closeLoader } from '../../components/loader/actions';
import { FormGroupTitleComponent } from '../../components/form-group-title';
// forms
import { FormGroupWrapper } from '../../forms';
import { Fieldset2 } from '../../forms/formik/fieldset2/fieldset';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// icons
import * as I from '../../svgs';
// ui
import { Box, Span, Flex, Grid, FixedFlex, RelativeBox, ModalCancelButton, scrollableContainerCss4px } from '../../ui';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
//////////////////////////////////////////////////

const renderScorecardRowValue = ({ value, symbol, percentValue, prefixSymbol }) => {
  if (G.isNilOrZero(value)) return <TextComponent color='dark.darkBlue'>N/A</TextComponent>;

  return (
    <TextComponent color='dark.darkBlue'>
      {
        G.isNotNil(percentValue) &&
        <Span mr={15} fontWeight='bold' color='dark.darkGrey'>({percentValue})%</Span>
      }
      {prefixSymbol} {value}{symbol}
    </TextComponent>
  );
};

const Scorecard = (props) => {
  const {
    telsCount,
    ratesTotalSum,
    onTimeTelsCount,
    invoiceTotalSum,
    averageRateTotal,
    onTimeDropsCount,
    onTimeTelsPercent,
    expiredRatesCount,
    acceptedTelsCount,
    onTimeDropsPercent,
    normalizedCurrency,
    declinedRatesCount,
    canceledRatesCount,
    onTimePickupsCount,
    expiredRatesPercent,
    averageInvoiceTotal,
    onTimePickupsPercent,
    canceledRatesPercent,
    declinedRatesPercent,
    acceptedRatesPercent,
    outstandingInvoicesCount,
    carrierCompletedEventCount,
    averageInvoiceSubmitMinutes,
    averageDocumentSubmitMinutes,
    dispatcherCompletedEventCount,
    transportationModeDisplayedValue,
  } = props;

  const currencySymbol = G.getCurrencySymbol(normalizedCurrency);

  const rows = [
    {
      value: acceptedTelsCount,
      percentValue: acceptedRatesPercent,
      text: ['titles:accepted-trips', 'Accepted Trips'],
    },
    {
      value: declinedRatesCount,
      percentValue: declinedRatesPercent,
      text: ['titles:declined-trips', 'Declined Trips'],
    },
    {
      value: canceledRatesCount,
      percentValue: canceledRatesPercent,
      text: ['titles:canceled-trips', 'Canceled Trips'],
    },
    {
      value: expiredRatesCount,
      percentValue: expiredRatesPercent,
      text: ['titles:expired-trips', 'Expired Trips'],
    },
    {
      prefixSymbol: currencySymbol,
      value: G.mathRoundNumber(averageRateTotal),
      text: ['titles:average-trip-total', 'Average Trip Total'],
    },
    {
      prefixSymbol: currencySymbol,
      value: G.mathRoundNumber(ratesTotalSum),
      text: ['titles:trip-total', 'Trip Total'],
    },
    {
      prefixSymbol: currencySymbol,
      value: G.mathRoundNumber(averageInvoiceTotal),
      text: ['titles:average-invoice-total', 'Average Invoice Total'],
    },
    {
      prefixSymbol: currencySymbol,
      value: G.mathRoundNumber(invoiceTotalSum),
      text: ['titles:invoice-total', 'Invoice Total'],
    },
    {
      value: onTimeTelsCount,
      percentValue: onTimeTelsPercent,
      text: ['titles:on-time-trips', 'On Time Trips'],
    },
    {
      value: dispatcherCompletedEventCount,
      text: ['titles:dispatcher-completed-events', 'Dispatcher Completed Events'],
    },
    {
      value: carrierCompletedEventCount,
      text: ['titles:carrier-completed-events', 'Carrier Completed Events'],
    },
    {
      value: onTimePickupsCount,
      percentValue: onTimePickupsPercent,
      text: ['titles:on-time-pickups', 'On Time Pickups'],
    },
    {
      value: onTimeDropsCount,
      percentValue: onTimeDropsPercent,
      text: ['titles:on-time-drops', 'On Time Drops'],
    },
    {
      symbol: ' min',
      value: G.mathRoundNumber(averageDocumentSubmitMinutes),
      text: ['titles:average-document-submit', 'Average Document Submit'],
    },
    {
      symbol: ' min',
      value: G.mathRoundNumber(averageInvoiceSubmitMinutes),
      text: ['titles:average-invoice-submit', 'Average Invoice Submit'],
    },
    {
      value: outstandingInvoicesCount,
      text: ['titles:outstanding-invoices', 'Outstanding Invoices'],
    },
  ];

  return (
    <Box
      pt={10}
      pb={20}
      bg='white'
      width='100%'
      borderRadius={16}
      // demo 5
      boxShadow='cardBoxShadow'
    >
      <Flex
        px={20}
        mb={15}
        flexWrap='wrap'
        fontWeight='bold'
        color='greyMatterhorn'
        justifyContent='space-between'
      >
        <TextComponent
          mt={10}
          maxWidth='100%'
          withEllipsis={true}
          title={transportationModeDisplayedValue}
        >
          {
            G.isNilOrEmpty(transportationModeDisplayedValue) &&
            G.getWindowLocale('titles:overall-total', 'Overall Total')
          }
          {
            G.isNotNilAndNotEmpty(transportationModeDisplayedValue) &&
            `${G.getWindowLocale('titles:mode-of-transportation', 'Mode Of Transportation')}: ${
              transportationModeDisplayedValue
            }`
          }
        </TextComponent>
        <TextComponent mt={10}>
          {G.getWindowLocale('titles:tel-count', 'TEL Count')}: {telsCount}
        </TextComponent>
      </Flex>
      <Box px={20} maxHeight={400} overflowY='auto' css={scrollableContainerCss4px}>
        {
          rows.map(({ text, value, symbol, prefixSymbol, percentValue }) => (
            <Flex
              p={10}
              mt={15}
              key={text[0]}
              fontWeight='bold'
              borderRadius={12}
              bg='transparentDarkBlue'
              justifyContent='space-between'
            >
              <TextComponent color='dark.darkGrey'>{G.getWindowLocale(...text)}</TextComponent>
              {renderScorecardRowValue({ value, symbol, percentValue, prefixSymbol })}
            </Flex>
          ))
        }
      </Box>
    </Box>
  );
};

const safetySettings = {
  [GC.FIELD_COUNTRY]: {
    width: 150,
    name: 'titles:country',
    customComponent: (row) => R.compose(
      R.join(' '),
      R.map(G.toTitleCase),
      R.split('_'),
      R.pathOr('', ['data', GC.FIELD_COUNTRY]),
    )(row),
  },
  driverNatlEvgPercentage: {
    width: 180,
    name: ['titles:driver', 'titles:natl-evg-percentage'],
  },
  driverOosPercentage: {
    width: 150,
    name: ['titles:driver', 'titles:oos-percentage'],
  },
  hazmatNatlEvgPercentage: {
    width: 180,
    name: ['titles:hazmat', 'titles:natl-evg-percentage'],
  },
  hazmatOosPercentage: {
    width: 150,
    name: ['titles:hazmat', 'titles:oos-percentage'],
  },
  iepNatlEvgPercentage: {
    width: 180,
    name: ['titles:iep', 'titles:natl-evg-percentage'],
  },
  iepOosPercentage: {
    width: 150,
    name: ['titles:iep', 'titles:oos-percentage'],
  },
  vehicleNatlEvgPercentage: {
    width: 180,
    name: ['titles:vehicle', 'titles:natl-evg-percentage'],
  },
  vehicleOosPercentage: {
    width: 150,
    name: ['titles:vehicle', 'titles:oos-percentage'],
  },
};

const safetyReport = {
  fields: G.mapIndexed((name, sequence) => ({ name, sequence }), R.keys(safetySettings)),
};

const safetyTableSettings = {
  checkboxCellWidth: 0,
  allowSelectItems: false,
};

const Safety = ({ itemList }) => {
  const [isOpened, setIsOpened] = useState(true);

  const handleToggle = useCallback(() => setIsOpened(R.not), []);

  return (
    <FormGroupWrapper isOpened={isOpened}>
      <FormGroupTitleComponent
        mb='0px'
        isOpened={isOpened}
        withArrowDown={true}
        onToggleFormGroup={handleToggle}
        text={G.getWindowLocale('titles:safety-scores', 'Safety Scores')}
      />
      {
        isOpened &&
        <Table
          itemList={itemList}
          report={safetyReport}
          columnSettings={safetySettings}
          tableSettings={safetyTableSettings}
        />
      }
    </FormGroupWrapper>
  );
};

const inputWrapperStyles = {
  mr: 30,
  width: 200,
  zIndex: 12,
};

const fieldSettings = [
  {
    type: 'datePicker',
    fieldName: 'fromDate',
    label: ['titles:date-from', 'Date From'],
    inputWrapperStyles: R.assoc('zIndex', 13, inputWrapperStyles),
    maxDate: G.subtractDateTime(G.getCurrentDate(), 1, GC.FIELD_DAYS),
  },
  {
    inputWrapperStyles,
    type: 'reactSelect',
    fieldName: 'filterType',
    options: 'filterTypeOptions',
    label: ['titles:filter-type', 'Filter Type'],
  },
  {
    type: 'reactSelect',
    fieldName: 'filterValue',
    options: 'filterValueOptions',
    label: ['titles:filter-value', 'Filter Value'],
    inputWrapperStyles: {
      width: 200,
      zIndex: 12,
      display: (props) => G.ifElse(
        R.isNil(R.path(['values', 'filterType'], props)),
        'none',
      ),
    },
  },
];

const makeFilterTypeOptions = (divisionGuid) => {
  const currentBranchGuid = G.getAmousCurrentBranchGuidFromWindow();

  const currentBranchFilterTypeOption = {
    [GC.FIELD_VALUE]: currentBranchGuid,
    [GC.FIELD_LABEL]: G.getWindowLocale('titles:by-current-branch', 'Current Branch'),
  };

  if (R.or(G.isNilOrEmpty(divisionGuid), R.equals(divisionGuid, currentBranchGuid))) {
    return R.of(Array, currentBranchFilterTypeOption);
  }

  return [
    currentBranchFilterTypeOption,
    {
      [GC.FIELD_VALUE]: divisionGuid,
      [GC.FIELD_LABEL]: G.getWindowLocale('titles:trip-branch', 'Trip Branch'),
    },
  ];
};

const CarrierScorecards = (props) => {
  const {
    pr,
    height,
    minHeight,
    overflowY,
    carrierGuid,
    carrierName,
    divisionGuid,
    expandedContainer,
    closeExpandedContainer,
  } = props;

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      filterType: null,
      filterValue: null,
      fromDate: G.subtractDateTimeWithFormat(G.getCurrentDate(), 3, 'months', GC.DEFAULT_DATE_FORMAT),
    },
  });

  const [divisions, setDivisions] = useState({});
  const [scorecard, setScorecard] = useState(null);
  const [normalizedCurrency, setNormalizedCurrency] = useState(GC.DEFAULT_UI_CURRENCY);

  const { values, setFieldValue } = formik;

  const { fromDate, filterType, filterValue } = values;

  const { safetyScores, statisticData, numberOfTrucks } = R.or(scorecard, {});

  const handleGetDivisions = useCallback(async (selectedFilterType) => {
    dispatch(openLoader());

    try {
      const endpoint = endpointsMap.getBranchListByType(selectedFilterType);

      const options = {
        params: {
          [GC.BRANCH_TYPE]: GC.BRANCH_TYPE_ENUM_DIVISION,
        },
      };

      const res = await sendRequest('get', endpoint, options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        const divisionOptions = R.map(
          ({ name, guid }) => ({ [GC.FIELD_VALUE]: guid, [GC.FIELD_LABEL]: name }),
          R.or(data, []),
        );

        setDivisions((currentDivisions) => (
          R.assoc(selectedFilterType, divisionOptions, currentDivisions)
        ));
      } else {
        G.handleException('error handleGetDivisions');
      }
    } finally {
      dispatch(closeLoader());
    }
  }, [dispatch]);

  const handleGetScorecardRequest = useCallback(async () => {
    dispatch(openLoader());

    try {
      const options = {
        data: { carrierGuid, fromDate, filterByEnterpriseGuid: filterValue },
      };

      const res = await sendRequest('post', endpointsMap.carrierScorecard, options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        setScorecard(data);
      } else {
        G.handleFailResponseSimple(res);
      }
    } finally {
      dispatch(closeLoader());
    }
  }, [dispatch, fromDate, carrierGuid, filterValue]);

  useEffect(() => {
    const getNormalizedCurrency = async () => {
      const options = {
        params: { [GC.BRANCH_GUID]: G.getAmousCurrentBranchGuidFromWindow() },
      };

      const res = await sendRequest('get', endpointsMap.branchNormalizedCurrency, options);

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        setNormalizedCurrency(data);
      } else {
        G.handleException('error getNormalizedCurrency');
      }
    };

    getNormalizedCurrency();
  }, []);

  useEffect(() => {
    if (R.or(R.isNil(filterType), R.has(filterType, divisions))) return;

    handleGetDivisions(filterType);
  }, [divisions, filterType, handleGetDivisions]);

  useEffect(() => {
    if (R.and(R.isNil(filterType), R.isNotNil(filterValue))) {
      setFieldValue('filterValue', null);
    }
  }, [filterType, filterValue, setFieldValue]);

  useEffect(() => {
    if (G.isValidDateWithFormat(fromDate, GC.DEFAULT_DATE_FORMAT)) {
      handleGetScorecardRequest();
    }
  }, [handleGetScorecardRequest]);

  return (
    <RelativeBox
      pr={pr}
      height={height}
      bg='light.mainLight'
      minHeight={minHeight}
      overflowY={overflowY}
    >
      <Flex pl={20} flexWrap='wrap'>
        <PageTitle
          mr={20}
          mt='5px'
          label={(
            <Flex ml='6px' cursor='pointer' onClick={handleGetScorecardRequest}>
              {I.reloadCircle(G.getTheme('colors.dark.mainDark'))}
            </Flex>
          )}
          display='flex'
          title={`${carrierName}${G.ifElse(G.isNotNilAndNotEmpty(numberOfTrucks), ` (${numberOfTrucks} Trucks)`, '')}`}
        />
        <Fieldset2
          {...G.getFormikProps(formik)}
          fields={fieldSettings}
          fieldsWrapperStyles={{ pt: 25 }}
          filterTypeOptions={makeFilterTypeOptions(divisionGuid)}
          filterValueOptions={R.pathOr([], [filterType], divisions)}
        />
      </Flex>
      {G.isNotNilAndNotEmpty(safetyScores) && <Safety itemList={safetyScores} />}
      {
        G.isNotNilAndNotEmpty(statisticData) &&
        <Box p={20} overflowY='auto'>
          <Grid gridGap={20} gridTemplateColumns='repeat(auto-fill, minmax(350px, 1fr))'>
            {
              statisticData.map((item) => (
                <Scorecard
                  {...item}
                  normalizedCurrency={normalizedCurrency}
                  key={R.propOr('overall-total', 'transportationModeDisplayedValue', item)}
                />
              ))
            }
          </Grid>
        </Box>
      }
      {
        G.isTrue(expandedContainer) &&
        <FixedFlex
          bottom='0'
          bg='white'
          zIndex={12}
          width='100%'
          p='14px 20px'
          boxShadow='standard'
        >
          <ModalCancelButton width={130} onClick={() => closeExpandedContainer(true)}>
            {G.getWindowLocale('actions:close', 'Close')}
          </ModalCancelButton>
        </FixedFlex>
      }
    </RelativeBox>
  );
};

export default CarrierScorecards;
